import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Shield } from 'lucide-react';
import { bugsData } from '@/data/bugsData';

const severityColors = {
  Critical: 'text-rose-400 bg-rose-400/10 border border-rose-400/20',
  High: 'text-orange-400 bg-orange-400/10 border border-orange-400/20',
  Medium: 'text-amber-400 bg-amber-400/10 border border-amber-400/20',
  Low: 'text-emerald-400 bg-emerald-400/10 border border-emerald-400/20',
};

const priorityColors = {
  P1: 'text-rose-400 bg-rose-400/10 border border-rose-400/20',
  P2: 'text-orange-400 bg-orange-400/10 border border-orange-400/20',
  P3: 'text-amber-400 bg-amber-400/10 border border-amber-400/20',
  P4: 'text-emerald-400 bg-emerald-400/10 border border-emerald-400/20',
};

export const BugList = () => {
  const bugs = bugsData;

  return (
    <section
      id="bugs"
      className="min-h-screen py-24 sm:py-32"
      data-testid="bugs-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2
                className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white mb-4"
                style={{ fontFamily: 'Outfit' }}
                data-testid="bugs-heading"
              >
                Production Bug Findings
              </h2>
              <p className="text-base leading-relaxed text-slate-300" style={{ fontFamily: 'IBM Plex Sans' }}>
                The findings below are based on my observations while using production applications and websites. They are shared purely as examples of my curiosity and passion for identifying potential issues and continuously sharpening my bug-finding instincts—no offense or criticism is intended toward the products or teams behind them.
              </p>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="flex items-start space-x-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-sm mb-6">
            <Shield className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-300" style={{ fontFamily: 'IBM Plex Sans' }}>
              I only share safe examples here—details for critical or exploitable bugs are always withheld.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-slate-900/30 border border-white/5 overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="bugs-table">
              <thead>
                <tr className="border-b border-white/5">
                  <th
                    className="py-4 px-6 text-left text-sm text-slate-400 uppercase tracking-[0.2em] font-medium"
                    style={{ fontFamily: 'IBM Plex Sans' }}
                  >
                    App Name
                  </th>
                  <th
                    className="py-4 px-6 text-left text-sm text-slate-400 uppercase tracking-[0.2em] font-medium"
                    style={{ fontFamily: 'IBM Plex Sans' }}
                  >
                    Severity
                  </th>
                  <th
                    className="py-4 px-6 text-left text-sm text-slate-400 uppercase tracking-[0.2em] font-medium"
                    style={{ fontFamily: 'IBM Plex Sans' }}
                  >
                    Priority
                  </th>
                  <th
                    className="py-4 px-6 text-left text-sm text-slate-400 uppercase tracking-[0.2em] font-medium"
                    style={{ fontFamily: 'IBM Plex Sans' }}
                  >
                    Type of Bug
                  </th>
                  <th
                    className="py-4 px-6 text-left text-sm text-slate-400 uppercase tracking-[0.2em] font-medium"
                    style={{ fontFamily: 'IBM Plex Sans' }}
                  >
                    Environment
                  </th>
                  <th
                    className="py-4 px-6 text-left text-sm text-slate-400 uppercase tracking-[0.2em] font-medium"
                    style={{ fontFamily: 'IBM Plex Sans' }}
                  >
                    Found Date
                  </th>
                  <th
                    className="py-4 px-6 text-left text-sm text-slate-400 uppercase tracking-[0.2em] font-medium"
                    style={{ fontFamily: 'IBM Plex Sans' }}
                  >
                    Detail
                  </th>
                </tr>
              </thead>
              <tbody>
                {bugs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="py-12 text-center text-slate-500">
                      No bugs reported yet.
                    </td>
                  </tr>
                ) : (
                  bugs.map((bug, index) => (
                    <tr
                      key={bug.id}
                      className={`border-b border-white/5 hover:bg-slate-800/40 transition-colors duration-200 ${index % 2 === 1 ? 'bg-slate-900/20' : 'bg-transparent'
                        }`}
                      data-testid={`bug-row-${index}`}
                    >
                      <td
                        className="py-4 px-6 text-white font-medium"
                        style={{ fontFamily: 'IBM Plex Sans' }}
                        data-testid={`bug-app-name-${index}`}
                      >
                        {bug.appName}
                      </td>
                      <td className="py-4 px-6">
                        <span
                          className={`px-3 py-1 rounded-sm text-xs font-medium ${severityColors[bug.severity]}`}
                          style={{ fontFamily: 'JetBrains Mono' }}
                          data-testid={`bug-severity-${index}`}
                        >
                          {bug.severity}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span
                          className={`px-3 py-1 rounded-sm text-xs font-medium ${priorityColors[bug.priority]}`}
                          style={{ fontFamily: 'JetBrains Mono' }}
                          data-testid={`bug-priority-${index}`}
                        >
                          {bug.priority}
                        </span>
                      </td>
                      <td
                        className="py-4 px-6 text-slate-300"
                        style={{ fontFamily: 'IBM Plex Sans' }}
                        data-testid={`bug-type-${index}`}
                      >
                        {bug.typeOfBug || '-'}
                      </td>
                      <td
                        className="py-4 px-6 text-slate-300"
                        style={{ fontFamily: 'IBM Plex Sans' }}
                        data-testid={`bug-environment-${index}`}
                      >
                        {bug.environment || '-'}
                      </td>
                      <td
                        className="py-4 px-6 text-slate-300 font-mono text-sm"
                        style={{ fontFamily: 'JetBrains Mono' }}
                        data-testid={`bug-found-date-${index}`}
                      >
                        {bug.foundDate}
                      </td>
                      <td className="py-4 px-6">
                        {bug.detail ? (
                          <a
                            href={bug.detail}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-emerald-400 hover:text-emerald-300 flex items-center space-x-1 transition-colors duration-200"
                            data-testid={`bug-detail-link-${index}`}
                          >
                            <span>View Details</span>
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <span className="text-slate-500">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
